import { useEffect, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { Building2, Plus, ArrowRight } from 'lucide-react';
import toast from 'react-hot-toast';
import { listAllOrgs } from '@/lib/firestore';
import { callCreateOrg } from '@/lib/functions';
import { fmtRelative } from '@/lib/utils';
import { useT } from '@/lib/i18n';
import { Loader } from '@/components/ui/loader';
import { Button } from '@/components/ui/button';
import { cardClassName } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog } from '@/components/ui/dialog';
import type { Organization } from '@/types/models';

export default function AdminOrgs() {
  const { t, locale } = useT();
  const [orgs, setOrgs] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(true);
  const [show, setShow] = useState(false);
  const [creating, setCreating] = useState(false);

  const refresh = async () => {
    setLoading(true);
    setOrgs(await listAllOrgs());
    setLoading(false);
  };
  useEffect(() => { refresh(); }, []);

  const onCreate = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    setCreating(true);
    try {
      await callCreateOrg({
        displayName:       String(f.get('displayName')),
        legalName:         String(f.get('legalName') ?? ''),
        primaryContactName: String(f.get('contactName')),
        primaryContactEmail: String(f.get('contactEmail')),
      });
      toast.success(t('admin.orgs.created'));
      setShow(false);
      refresh();
    } catch (e) { toast.error((e as Error).message); }
    finally { setCreating(false); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="font-display text-2xl text-ink-900 dark:text-white">{t('admin.orgs.title')}</h1>
        <Button onClick={() => setShow(true)}><Plus className="h-4 w-4" /> {t('admin.orgs.new')}</Button>
      </div>

      {loading ? <Loader /> : (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          {orgs.map(o => (
            <Link key={o.id} to={`/admin/orgs/${o.id}`} className={cardClassName('flex items-start gap-4')}>
              <div className="grid h-10 w-10 place-items-center rounded-lg bg-brand-50 text-brand-600 dark:bg-brand-900/30 dark:text-brand-300">
                <Building2 className="h-5 w-5" />
              </div>
              <div className="flex-1">
                <div className="font-medium text-ink-900 dark:text-white">{o.displayName}</div>
                <div className="text-xs text-ink-500">{o.primaryContact?.email ?? '—'} · {t('admin.orgs.created_at', { when: fmtRelative(o.createdAt, t, locale) })}</div>
              </div>
              <ArrowRight className="h-4 w-4 text-ink-400" />
            </Link>
          ))}
        </div>
      )}

      <Dialog
        open={show}
        onClose={() => setShow(false)}
        title={t('admin.orgs.modal.title')}
        footer={
          <>
            <Button type="button" variant="secondary" onClick={() => setShow(false)}>{t('common.cancel')}</Button>
            <Button type="submit" form="create-org-form" disabled={creating}>{t('admin.orgs.create_cta')}</Button>
          </>
        }
      >
        <form id="create-org-form" onSubmit={onCreate} className="grid gap-4">
          <div>
            <Label>{t('admin.orgs.modal.name')}</Label>
            <Input name="displayName" required className="mt-1" placeholder="ACME Inc." />
          </div>
          <div>
            <Label>{t('admin.orgs.modal.legal')}</Label>
            <Input name="legalName" className="mt-1" />
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <Label>{t('admin.orgs.modal.contact_name')}</Label>
              <Input name="contactName" required className="mt-1" />
            </div>
            <div>
              <Label>{t('admin.orgs.modal.contact_email')}</Label>
              <Input type="email" name="contactEmail" required className="mt-1" />
            </div>
          </div>
        </form>
      </Dialog>
    </div>
  );
}
